import { axiosInstance } from "@shared/api";
import {
  attach,
  createEffect,
  createEvent,
  createStore,
  sample,
} from "effector";
import {
  CreditTypeEnum,
  IApplicationDataFinalStep,
  IApplicationDataStep1,
  IApplicationDataStep2,
  IApplicationForm,
  IApplicationFormStep1,
  IApplicationFormStep1Code,
  IApplicationFormStep2,
  IApplicationFormStep3,
  IApplicationFormStep4,
  IApplicationFormStep5,
  ICollateralFields,
  ILoanFields,
  INewCarFields,
  IRealEstateFields,
  IUsedCarFields,
} from "@entities/Product/Application/types";
import { verifyPhoneByCodeFx } from "@shared/model";
import { extractNumbers } from "@shared/lib";
import { IDir, IVerifyPhoneData } from "@shared/types";

export const sendVerificationCode = createEffect(
  async (data: IVerifyPhoneData) => {
    await verifyPhoneByCodeFx(data);
  }
);

export const resendVerificationCode = createEffect(
  async (data: IVerifyPhoneData) => {
    await verifyPhoneByCodeFx(data);
  }
);
export const sendApplicationDataStep1 = createEffect(
  async (data: IApplicationDataStep1) => {
    const response = await axiosInstance.post<{}>("/applications/step-1", data);
    return response.data;
  }
);

export const sendApplicationDataStep2 = createEffect(
  async (data: IApplicationDataStep2) => {
    const response = await axiosInstance.post<{}>("/applications/step-2", data);
    return response.data;
  }
);

export const sendApplicationDataFinalStep = createEffect(
  async (data: IApplicationDataFinalStep) => {
    const response = await axiosInstance.post<{}>(
      "/applications/step-complete",
      data
    );
    return response.data;
  }
);

export const getDistrictsFx = createEffect(async (stateCode: string) => {
  const response = await axiosInstance.get<IDir[]>(
    `/dir/cities/${stateCode}/districts`
  );
  return response.data;
});

export const getAddressesFx = createEffect(
  async (params: { stateCode: string; districtCode: string }) => {
    const response = await axiosInstance.get<IDir[]>(
      `/dir/cities/${params.stateCode}/districts/${params.districtCode}/addresses`
    );
    return response.data;
  }
);

export const getCarTypesFx = createEffect(async (brandCode: string) => {
  const response = await axiosInstance.get<IDir[]>(
    `/dir/brands/${brandCode}/types`
  );
  return response.data;
});

export const getCarModelsFx = createEffect(
  async (params: { typeCode: string; brandCode: string }) => {
    const response = await axiosInstance.get<IDir[]>(
      `/dir/brands/${params.brandCode}/types/${params.typeCode}/models`
    );
    return response.data;
  }
);

export const getCarTypesCollateralFx = attach({
  effect: getCarTypesFx,
  mapParams: (params) => params,
});

export const getCarModelsCollateralFx = attach({
  effect: getCarModelsFx,
  mapParams: (params) => params,
});
export const getCarTypesCreditFx = attach({
  effect: getCarTypesFx,
  mapParams: (params) => params,
});

export const getCarModelsCreditFx = attach({
  effect: getCarModelsFx,
  mapParams: (params) => params,
});
export const resetApplicationForm = createEvent();
export const setApplicationStep = createEvent<number>();
export const setApplicationStepPlusTwo = createEvent<number>();
export const setApplicationStepPlusOne = createEvent<number>();
export const $applicationStep = createStore(0)
  .on(setApplicationStep, (_, payload) => payload)
  .on(setApplicationStepPlusTwo, (state) => state + 2)
  .on(setApplicationStepPlusOne, (state) => state + 1)
  .reset(resetApplicationForm);

export const setSuccessModalVisibility = createEvent<boolean>();

export const $successModalVisibility = createStore(false).on(
  setSuccessModalVisibility,
  (_, payload) => payload
);

export const $realEstateDistricts = createStore<
  { value: string; label: string }[]
>([]).on(getDistrictsFx.doneData, (state, payload) =>
  payload.map((item) => ({
    value: String(item.code),
    label: item.name,
  }))
);

export const $realEstateAddresses = createStore<
  { value: string; label: string }[]
>([]).on(getAddressesFx.doneData, (state, payload) =>
  payload.map((item) => ({
    value: String(item.code),
    label: item.name,
  }))
);

export const $creditCarTypes = createStore<{ value: string; label: string }[]>(
  []
).on(getCarTypesCreditFx.doneData, (state, payload) =>
  payload.map((item) => ({
    value: String(item.code),
    label: item.name,
  }))
);

export const $creditCarModels = createStore<{ value: string; label: string }[]>(
  []
).on(getCarModelsCreditFx.doneData, (state, payload) =>
  payload.map((item) => ({
    value: String(item.code),
    label: item.name,
  }))
);

export const $collateralCarTypes = createStore<
  { value: string; label: string }[]
>([]).on(getCarTypesCollateralFx.doneData, (state, payload) =>
  payload.map((item) => ({
    value: String(item.code),
    label: item.name,
  }))
);

export const $collateralCarModels = createStore<
  { value: string; label: string }[]
>([]).on(getCarModelsCollateralFx.doneData, (state, payload) =>
  payload.map((item) => ({
    value: String(item.code),
    label: item.name,
  }))
);

// step 1
const applicationStep1InitialValues: IApplicationFormStep1 = {
  phone: "",
  agreement: false,
};
export const submitApplicationStep1 = createEvent<IApplicationFormStep1>();
export const $applicationFormStep1 = createStore<IApplicationFormStep1>(
  applicationStep1InitialValues
)
  .on(submitApplicationStep1, (state, payload) => payload)
  .reset(resetApplicationForm);

// step 1 code
const applicationStep1CodeInitialValues: IApplicationFormStep1Code = {
  verification_code: "",
};
export const submitApplicationStep1Code =
  createEvent<IApplicationFormStep1Code>();
export const $applicationFormStep1Code = createStore<IApplicationFormStep1Code>(
  applicationStep1CodeInitialValues
)
  .on(submitApplicationStep1Code, (state, payload) => payload)
  .reset(resetApplicationForm);

// step 2 code
const applicationStep2InitialValues: IApplicationFormStep2 = {
  name: "",
  last_name: "",
  middle_name: "",
  iin: "",
  city: "",
};
export const submitApplicationStep2 = createEvent<IApplicationFormStep2>();
export const setCityApplicationStep2 = createEvent<string>();
export const $applicationFormStep2 = createStore<IApplicationFormStep2>(
  applicationStep2InitialValues
)
  .on(submitApplicationStep2, (state, payload) => payload)
  .on(setCityApplicationStep2, (state, payload) => ({
    ...state,
    city: payload,
  }))
  .reset(resetApplicationForm);

// step 3
const applicationStep3InitialValues: IApplicationFormStep3 = {
  credit_type: "",
  credit_sum: "",
  credit_monthly_payment: "",
  credit_period: "",
  credit_target: "",
  credit_car_brand: "",
  credit_car_name: "",
  credit_car_type: "",
  credit_car_year: "",
  credit_car_doc_number: "",
  credit_car_price: "",
  advance_payment: "",
};
export const submitApplicationStep3 = createEvent<IApplicationFormStep3>();
export const setCreditTypeApplicationStep3 = createEvent<string>();
export const $applicationFormStep3 = createStore<IApplicationFormStep3>(
  applicationStep3InitialValues
)
  .on(submitApplicationStep3, (state, payload) => payload)
  .on(setCreditTypeApplicationStep3, (state, payload) => ({
    ...state,
    credit_type: payload,
  }))
  .reset(resetApplicationForm);

sample({
  source: {
    step1: $applicationFormStep1,
    step1code: $applicationFormStep1Code,
    step2: $applicationFormStep2,
    step3: $applicationFormStep3,
  },
  clock: submitApplicationStep3,
  fn: ({ step1, step1code, step2, step3 }) => {
    const result = {
      verification_code: step1code.verification_code,
      product_id: step3.credit_type,
      personal: {
        phone: String(extractNumbers(step1.phone)),
        name: step2.name,
        iin: step2.iin,
        last_name: step2.last_name,
        middle_name: step2.middle_name,
        city_id: step2.city,
      },
      loan: {},
    };

    const loan: ILoanFields = {
      credit_sum: String(extractNumbers(step3.credit_sum)),
      credit_monthly_payment: String(
        extractNumbers(step3.credit_monthly_payment)
      ),
      credit_period_id: step3.credit_period,
      credit_target_id: step3.credit_target,
    };

    const newAutoFields: INewCarFields = {
      price_of_car: String(extractNumbers(step3.credit_car_price)),
      brand_name: step3.credit_car_brand,
      model_name: step3.credit_car_name,
      type_name: step3.credit_car_type,
    };

    const usedAutoFields: IUsedCarFields = {
      ...newAutoFields,
      production_year: String(step3.credit_car_year),
      technical_passport: step3.credit_car_doc_number,
    };

    if (result.product_id === CreditTypeEnum.NEW_AUTO) {
      Object.assign(loan, {
        ...newAutoFields,
        advance_payment: String(extractNumbers(step3.advance_payment)),
      });
    }

    if (result.product_id === CreditTypeEnum.USED_AUTO) {
      Object.assign(loan, {
        ...usedAutoFields,
        advance_payment: String(extractNumbers(step3.advance_payment)),
      });
    }

    return { ...result, loan } as IApplicationDataStep1;
  },
  target: sendApplicationDataStep1,
});

// step 4
const applicationStep4InitialValues = {
  company_name: "",
  classification: "",
  position: "",
  working_period: "",
  month_income: "",
};
export const submitApplicationStep4 = createEvent<IApplicationFormStep4>();
export const setClassificationApplicationStep4 = createEvent<string>();
export const $applicationFormStep4 = createStore<IApplicationFormStep4>(
  applicationStep4InitialValues
)
  .on(submitApplicationStep4, (state, payload) => payload)
  .on(setClassificationApplicationStep4, (state, payload) => ({
    ...state,
    classification: payload,
  }))
  .reset(resetApplicationForm);

sample({
  clock: submitApplicationStep4,
  source: {
    step1: $applicationFormStep1,
    step1code: $applicationFormStep1Code,
    step2: $applicationFormStep2,
    step3: $applicationFormStep3,
    step4: $applicationFormStep4,
  },
  fn: ({ step1, step1code, step2, step3, step4 }) => {
    const result = {
      verification_code: step1code.verification_code,
      product_id: step3.credit_type,
      personal: {
        phone: String(extractNumbers(step1.phone)),
        name: step2.name,
        iin: step2.iin,
        last_name: step2.last_name,
        middle_name: step2.middle_name,
        city_id: step2.city,
      },
      loan: {},
      job: {
        company_name: step4.company_name,
        classification_id: step4.classification,
        position: step4.position,
        month_income: String(extractNumbers(step4.month_income)),
        working_period: step4.working_period,
      },
    };

    const loan: ILoanFields = {
      credit_sum: String(extractNumbers(step3.credit_sum)),
      credit_monthly_payment: String(
        extractNumbers(step3.credit_monthly_payment)
      ),
      credit_period_id: step3.credit_period,
      credit_target_id: step3.credit_target,
    };

    const newAutoFields: INewCarFields = {
      price_of_car: String(extractNumbers(step3.credit_car_price)),
      brand_name: step3.credit_car_brand,
      model_name: step3.credit_car_name,
      type_name: step3.credit_car_type,
    };

    const usedAutoFields: IUsedCarFields = {
      ...newAutoFields,
      production_year: String(step3.credit_car_year),
      technical_passport: step3.credit_car_doc_number,
    };

    if (result.product_id === CreditTypeEnum.NEW_AUTO) {
      Object.assign(loan, {
        ...newAutoFields,
        advance_payment: String(extractNumbers(step3.advance_payment)),
      });
    }

    if (result.product_id === CreditTypeEnum.USED_AUTO) {
      Object.assign(loan, {
        ...usedAutoFields,
        advance_payment: String(extractNumbers(step3.advance_payment)),
      });
    }

    return { ...result, loan } as IApplicationDataStep2;
  },
  target: sendApplicationDataStep2,
});

sample({
  clock: sendApplicationDataStep2.done,
  source: {
    currentStep: $applicationStep,
    step3: $applicationFormStep3,
  },
  fn: ({ step3, currentStep }) => {
    const hasStep5 = [
      CreditTypeEnum.PLEDGE_REAL_ESTATE,
      CreditTypeEnum.PLEDGE_AUTO,
    ].includes(step3.credit_type as CreditTypeEnum);
    if (hasStep5) {
      return currentStep + 1;
    }
    return currentStep + 2;
  },
  target: setApplicationStep,
});

const applicationStep5InitialValues: IApplicationFormStep5 = {
  pledge_type: "",
  real_estate_price: "",
  real_estate_address_state: "",
  real_estate_address_district: "",
  real_estate_address_village: "",
  real_estate_full_address: "",
  collateral_car_brand: "",
  collateral_car_name: "",
  collateral_car_type: "",
  collateral_car_year: "",
  collateral_car_doc_number: "",
  collateral_car_price: "",
};
export const submitApplicationStep5 = createEvent<IApplicationFormStep5>();
export const $applicationFormStep5 = createStore<IApplicationFormStep5>(
  applicationStep5InitialValues
)
  .on(submitApplicationStep5, (state, payload) => payload)
  .reset(resetApplicationForm);

sample({
  source: $applicationStep,
  clock: [
    sendVerificationCode.done,
    sendApplicationDataStep1.done,
    submitApplicationStep2,
    submitApplicationStep5,
  ],
  target: setApplicationStepPlusOne,
});

const applicationFormInitialState: IApplicationForm = {
  ...applicationStep1InitialValues,
  ...applicationStep1CodeInitialValues,
  ...applicationStep2InitialValues,
  ...applicationStep3InitialValues,
  ...applicationStep4InitialValues,
  ...applicationStep5InitialValues,
};

export const submitApplicationFinalStep = createEvent<IApplicationForm>();
export const $applicationFormFinalStep = createStore<IApplicationForm>(
  applicationFormInitialState
)
  .on(
    [
      $applicationFormStep1,
      $applicationFormStep1Code,
      $applicationFormStep2,
      $applicationFormStep3,
      $applicationFormStep4,
      $applicationFormStep5,
      submitApplicationFinalStep,
    ],
    (state, payload) => ({ ...state, ...payload })
  )
  .reset(resetApplicationForm);

sample({
  source: {
    state: $applicationFormFinalStep,
  },
  clock: submitApplicationFinalStep,
  fn: ({ state }, clock) => {
    const result = {
      verification_code: state.verification_code,
      product_id: clock.credit_type,
      personal: {
        phone: String(extractNumbers(state.phone)),
        name: clock.name,
        iin: clock.iin,
        last_name: clock.last_name,
        middle_name: clock.middle_name,
        city_id: clock.city,
      },
      loan: {},
      job: {
        company_name: clock.company_name,
        classification_id: clock.classification,
        position: clock.position,
        month_income: String(extractNumbers(clock.month_income)),
        working_period: clock.working_period,
      },
    };

    const loan: ILoanFields = {
      credit_sum: String(extractNumbers(clock.credit_sum)),
      credit_monthly_payment: String(
        extractNumbers(clock.credit_monthly_payment)
      ),
      credit_period_id: clock.credit_period,
      credit_target_id: clock.credit_target,
    };

    const collateral: ICollateralFields = {};

    const newAutoFields: INewCarFields = {
      price_of_car: String(extractNumbers(clock.credit_car_price)),
      brand_name: clock.credit_car_brand,
      model_name: clock.credit_car_name,
      type_name: clock.credit_car_type,
    };

    const usedAutoFields: IUsedCarFields = {
      ...newAutoFields,
      production_year: String(clock.credit_car_year),
      technical_passport: clock.credit_car_doc_number,
    };

    const collateralAutoFields: IUsedCarFields = {
      price_of_car: String(extractNumbers(clock.collateral_car_price)),
      brand_name: clock.collateral_car_brand,
      model_name: clock.collateral_car_name,
      type_name: clock.collateral_car_type,
      production_year: String(clock.collateral_car_year),
      technical_passport: clock.collateral_car_doc_number,
    };

    const collateralRealEstateFields: IRealEstateFields = {
      price_of_real_estate: String(extractNumbers(clock.real_estate_price)),
      state: clock.real_estate_address_state,
      district: clock.real_estate_address_district,
      village: clock.real_estate_address_village,
      detail_address: clock.real_estate_full_address,
      type_of_collateral: clock.pledge_type,
    };

    if (result.product_id === CreditTypeEnum.NEW_AUTO) {
      Object.assign(loan, {
        ...newAutoFields,
        advance_payment: String(extractNumbers(clock.advance_payment)),
      });
    }

    if (result.product_id === CreditTypeEnum.USED_AUTO) {
      Object.assign(loan, {
        ...usedAutoFields,
        advance_payment: String(extractNumbers(clock.advance_payment)),
      });
    }

    if (result.product_id === CreditTypeEnum.PLEDGE_REAL_ESTATE) {
      Object.assign(collateral, collateralRealEstateFields);
    }

    if (result.product_id === CreditTypeEnum.PLEDGE_AUTO) {
      Object.assign(collateral, collateralAutoFields);
    }

    if (Object.keys(collateral).length) {
      return {
        ...result,
        loan,
        collateral,
      };
    }

    return {
      ...result,
      loan,
    } as IApplicationDataFinalStep;
  },
  target: sendApplicationDataFinalStep,
});

sample({
  clock: sendApplicationDataFinalStep.done,
  fn: () => true,
  target: setSuccessModalVisibility,
});

sample({
  clock: setSuccessModalVisibility,
  filter: (clock) => !clock,
  fn: () => 0,
  target: setApplicationStep,
});
